/**
 * Arriver déjà connecté sur le site d'une autre marque de la ville (retour de Luc du 04/10, docs/ux-v2/HANDOFF.md).
 *
 * S = site où le client est connecté, D = site de l'autre marque (même ville).
 * 1. Sur S, decorateCrossSiteUrl (appelé par openCrossSite, ./crossSite.ts) ajoute à la route de l'app le marqueur
 *    `mm_sso=<site de S>` (sans secret) quand le handoff est configuré et le client connecté.
 * 2. Sur D, main.tsx appelle captureHandoffFromUrl (./handoffCapture.ts) AVANT l'init de Sentry : marqueur et jeton
 *    quittent l'adresse.
 * 3. Sur D, mountV2 appelle runPendingHandoff avant de rendre l'app :
 *    - marqueur, pas de session sur D : POST handoff/start (nonce dans la session de D), puis navigation vers
 *      l'adresse d'autorisation de S, qui revient sur D avec `mm_handoff=<jeton>` (ou sans, si S refuse) ;
 *    - jeton : POST handoff/consume ; succès → mondayMindbody.user = fiche du client, l'app démarre connectée.
 * Tout échec laisse l'app démarrer normalement (déconnectée, sur la bonne route). Rien n'est jamais journalisé.
 */

import { SSO_PARAM, takeCapturedHandoff } from './handoffCapture';

const HEADER = 'X-MM-Handoff';

/** = UxV2Handoff::ROUTE_PATTERN. */
const ROUTE_PATTERN = /^(?!.*\/\/)\/[A-Za-z0-9/_-]{0,199}$/;

const SITE_PATTERN = /^[1-9]\d{0,9}$/;

const SESSION_COOKIE = /(?:^|;\s*)mm_session=1(?:;|$)/;

/**
 * Délai des deux POST. Large : un consume abandonné côté navigateur alors que le serveur a ouvert la session (nouvel
 * identifiant de session) laisse le visiteur déconnecté. Constaté en local (serveur lent) : 37 s pour un consume.
 */
const REQUEST_TIMEOUT_MS = 30_000;

/** Après le départ vers S : si la page n'est pas partie (navigation bloquée), l'app démarre quand même. */
const NAVIGATION_GRACE_MS = 10_000;

/**
 * Ajoute `name=value` à la route d'une adresse de l'app (`https://…/reservation/#/class/1`). Sans fragment de route
 * (`#/…`), l'adresse est rendue telle quelle.
 */
export function addRouteParam(url: string, name: string, value: string): string {
  const at = url.indexOf('#');
  if (at < 0 || url.charAt(at + 1) !== '/') return url;
  const hash = url.slice(at);
  const separator = hash.includes('?') ? '&' : '?';
  return `${url}${separator}${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
}

/* --------------------------------------------------------------------------------------------- état du site */

/** Le handoff est-il configuré sur ce site (constante de wp-config + v2) ? */
export function isHandoffEnabled(): boolean {
  return typeof mondayMindbody !== 'undefined' && mondayMindbody?.uxV2?.handoff === true;
}

function currentSiteId(): string {
  return typeof mondayMindbody !== 'undefined' ? String(mondayMindbody?.siteId ?? '') : '';
}

function regionalSiteIds(): string[] {
  const ids = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.regionalSiteIds : undefined;
  return Array.isArray(ids) ? ids.map((id) => String(id)) : [];
}

/**
 * Client connecté sur CE site : cookie `mm_session=1` (posé par le plugin à la connexion et retiré à la déconnexion,
 * y compris dans l'app sans rechargement, UxV2Session), ou fiche injectée dans la page.
 */
export function isLoggedInHere(): boolean {
  if (typeof document !== 'undefined' && SESSION_COOKIE.test(document.cookie)) return true;
  return typeof mondayMindbody !== 'undefined' && Boolean(mondayMindbody?.user?.Id);
}

/* ------------------------------------------------------------------------------------------------- site S */

/**
 * Adresse à charger dans le nouvel onglet vers le site `targetSiteId` (appelé par openCrossSite, ./crossSite.ts).
 * Ajoute le marqueur `mm_sso` si le handoff est configuré ici, le client connecté ici, et la cible un autre site de
 * la ville ; sinon l'adresse telle quelle. Aucun appel réseau : l'émission du jeton se fait au retour de D (§ 3.2).
 */
export async function decorateCrossSiteUrl(href: string, targetSiteId: string): Promise<string> {
  const current = currentSiteId();
  const target = String(targetSiteId);
  if (!isHandoffEnabled() || !isLoggedInHere() || !SITE_PATTERN.test(current) || target === current) return href;
  if (!regionalSiteIds().includes(target)) return href;
  return addRouteParam(href, SSO_PARAM, current);
}

/* ------------------------------------------------------------------------------------------------- site D */

/**
 * mountV2, avant le rendu : poursuit un handoff capturé dans l'adresse. null : rien à faire (rendu immédiat) ; sinon
 * une promesse résolue quand l'app peut démarrer (jamais rejetée).
 */
export function runPendingHandoff(): Promise<void> | null {
  const pending = takeCapturedHandoff();
  if (!isHandoffEnabled()) return null;
  if (pending.token) return consume(pending.token);
  if (pending.src && pending.src !== currentSiteId() && !mondayMindbody?.user?.Id) return start(pending.src);
  return null;
}

/** Route courante de l'app (fragment déjà nettoyé), validée comme côté serveur ; `/` à défaut. */
export function currentRoute(hash: string = typeof window !== 'undefined' ? window.location.hash : ''): string {
  const route = hash.replace(/^#/, '').split('?')[0] || '/';
  return ROUTE_PATTERN.test(route) ? route : '/';
}

async function start(src: string): Promise<void> {
  const data = await post('handoff/start', { src: Number(src), to: currentRoute() });
  const url = data && data.status === 'redirect' && typeof data.url === 'string' ? httpUrl(data.url) : null;
  if (!url) return;
  navigateWithOriginReferrer(url);
  await new Promise((resolve) => window.setTimeout(resolve, NAVIGATION_GRACE_MS));
}

async function consume(token: string): Promise<void> {
  const data = await post('handoff/consume', { token });
  if (data && data.status === 'ok' && data.user && typeof data.user === 'object' && 'Id' in data.user) {
    mondayMindbody.user = data.user as NonNullable<typeof mondayMindbody.user>;
  }
}

/**
 * Navigation vers S par un lien `referrerpolicy="origin"` : S reçoit l'origine de D en Referer (il refuse d'émettre
 * sans), quelle que soit la politique de la page. Une entrée d'historique : « précédent » ramène sur la fiche.
 */
function navigateWithOriginReferrer(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.referrerPolicy = 'origin';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
}

function httpUrl(value: string): string | null {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

function apiBase(): string {
  const base = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.apiBaseUrl : undefined;
  return (base || `${window.location.origin}/wp-json/mindbody/v1`).replace(/\/+$/, '');
}

/** POST JSON vers une route du handoff de CE site ; null sur toute erreur (jamais d'exception, jamais de trace). */
async function post(route: string, body: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = window.setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiBase()}/${route}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', [HEADER]: '1' },
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}
